/**
 * Searchable Ingredients Index
 *
 * A curated list of ~100 common ingredients for search suggestions.
 * Supports bilingual search (French/English) with category-based coloring.
 */

export type IngredientCategory =
  | 'protein'
  | 'vegetable'
  | 'fruit'
  | 'dairy'
  | 'grain'
  | 'pantry'
  | 'herb'
  | 'condiment';

export interface SearchableIngredient {
  id: string;
  displayName: string; // Bilingual display: "Chicken / Poulet"
  searchTerms: string[]; // All terms that match this ingredient
  category: IngredientCategory;
}

/**
 * Curated list of common ingredients with bilingual support
 */
export const SEARCHABLE_INGREDIENTS: SearchableIngredient[] = [
  // === PROTEINS ===
  {
    id: 'chicken',
    displayName: 'Chicken / Poulet',
    searchTerms: ['chicken', 'poulet', 'chicken breast', 'poitrine de poulet', 'chicken thigh', 'cuisse de poulet', 'chicken leg', 'chicken wing'],
    category: 'protein',
  },
  {
    id: 'beef',
    displayName: 'Beef / Boeuf',
    searchTerms: ['beef', 'boeuf', 'bœuf', 'steak', 'ground beef', 'boeuf haché', 'roast beef', 'rôti de boeuf'],
    category: 'protein',
  },
  {
    id: 'pork',
    displayName: 'Pork / Porc',
    searchTerms: ['pork', 'porc', 'pork chop', 'côtelette de porc', 'bacon', 'ham', 'jambon', 'sausage', 'saucisse'],
    category: 'protein',
  },
  {
    id: 'fish',
    displayName: 'Fish / Poisson',
    searchTerms: ['fish', 'poisson', 'salmon', 'saumon', 'cod', 'morue', 'tilapia', 'trout', 'truite', 'tuna', 'thon'],
    category: 'protein',
  },
  {
    id: 'shrimp',
    displayName: 'Shrimp / Crevettes',
    searchTerms: ['shrimp', 'crevettes', 'crevette', 'prawns', 'scallops', 'pétoncles'],
    category: 'protein',
  },
  {
    id: 'eggs',
    displayName: 'Eggs / Oeufs',
    searchTerms: ['eggs', 'egg', 'oeufs', 'oeuf', 'œufs', 'œuf'],
    category: 'protein',
  },
  {
    id: 'tofu',
    displayName: 'Tofu',
    searchTerms: ['tofu', 'tempeh', 'seitan'],
    category: 'protein',
  },
  {
    id: 'turkey',
    displayName: 'Turkey / Dinde',
    searchTerms: ['turkey', 'dinde', 'ground turkey', 'dinde hachée'],
    category: 'protein',
  },
  {
    id: 'lamb',
    displayName: 'Lamb / Agneau',
    searchTerms: ['lamb', 'agneau', 'lamb chop', 'côtelette d\'agneau'],
    category: 'protein',
  },

  // === VEGETABLES ===
  {
    id: 'onion',
    displayName: 'Onion / Oignon',
    searchTerms: ['onion', 'onions', 'oignon', 'oignons', 'red onion', 'oignon rouge', 'green onion', 'oignon vert', 'shallot', 'échalote'],
    category: 'vegetable',
  },
  {
    id: 'garlic',
    displayName: 'Garlic / Ail',
    searchTerms: ['garlic', 'ail', 'garlic clove', 'gousse d\'ail'],
    category: 'vegetable',
  },
  {
    id: 'tomato',
    displayName: 'Tomato / Tomate',
    searchTerms: ['tomato', 'tomatoes', 'tomate', 'tomates', 'cherry tomato', 'tomate cerise', 'roma tomato'],
    category: 'vegetable',
  },
  {
    id: 'carrot',
    displayName: 'Carrot / Carotte',
    searchTerms: ['carrot', 'carrots', 'carotte', 'carottes'],
    category: 'vegetable',
  },
  {
    id: 'potato',
    displayName: 'Potato / Pomme de terre',
    searchTerms: ['potato', 'potatoes', 'pomme de terre', 'pommes de terre', 'patate', 'patates', 'sweet potato', 'patate douce'],
    category: 'vegetable',
  },
  {
    id: 'bell-pepper',
    displayName: 'Bell Pepper / Poivron',
    searchTerms: ['bell pepper', 'pepper', 'poivron', 'poivrons', 'red pepper', 'poivron rouge', 'green pepper', 'poivron vert', 'yellow pepper'],
    category: 'vegetable',
  },
  {
    id: 'broccoli',
    displayName: 'Broccoli / Brocoli',
    searchTerms: ['broccoli', 'brocoli'],
    category: 'vegetable',
  },
  {
    id: 'spinach',
    displayName: 'Spinach / Épinards',
    searchTerms: ['spinach', 'épinards', 'épinard', 'baby spinach'],
    category: 'vegetable',
  },
  {
    id: 'lettuce',
    displayName: 'Lettuce / Laitue',
    searchTerms: ['lettuce', 'laitue', 'romaine', 'iceberg', 'salad greens', 'salade'],
    category: 'vegetable',
  },
  {
    id: 'cucumber',
    displayName: 'Cucumber / Concombre',
    searchTerms: ['cucumber', 'concombre', 'concombres'],
    category: 'vegetable',
  },
  {
    id: 'zucchini',
    displayName: 'Zucchini / Courgette',
    searchTerms: ['zucchini', 'courgette', 'courgettes'],
    category: 'vegetable',
  },
  {
    id: 'mushroom',
    displayName: 'Mushroom / Champignon',
    searchTerms: ['mushroom', 'mushrooms', 'champignon', 'champignons', 'portobello', 'shiitake'],
    category: 'vegetable',
  },
  {
    id: 'celery',
    displayName: 'Celery / Céleri',
    searchTerms: ['celery', 'céleri', 'celery stalk'],
    category: 'vegetable',
  },
  {
    id: 'corn',
    displayName: 'Corn / Maïs',
    searchTerms: ['corn', 'maïs', 'mais', 'corn on the cob', 'épi de maïs'],
    category: 'vegetable',
  },
  {
    id: 'peas',
    displayName: 'Peas / Petits pois',
    searchTerms: ['peas', 'petits pois', 'pois', 'green peas', 'snow peas', 'pois mange-tout'],
    category: 'vegetable',
  },
  {
    id: 'green-beans',
    displayName: 'Green Beans / Haricots verts',
    searchTerms: ['green beans', 'haricots verts', 'haricot vert', 'string beans'],
    category: 'vegetable',
  },
  {
    id: 'cabbage',
    displayName: 'Cabbage / Chou',
    searchTerms: ['cabbage', 'chou', 'red cabbage', 'chou rouge', 'napa cabbage'],
    category: 'vegetable',
  },
  {
    id: 'cauliflower',
    displayName: 'Cauliflower / Chou-fleur',
    searchTerms: ['cauliflower', 'chou-fleur', 'chou fleur'],
    category: 'vegetable',
  },
  {
    id: 'eggplant',
    displayName: 'Eggplant / Aubergine',
    searchTerms: ['eggplant', 'aubergine', 'aubergines'],
    category: 'vegetable',
  },
  {
    id: 'asparagus',
    displayName: 'Asparagus / Asperges',
    searchTerms: ['asparagus', 'asperges', 'asperge'],
    category: 'vegetable',
  },
  {
    id: 'beets',
    displayName: 'Beets / Betteraves',
    searchTerms: ['beets', 'beet', 'betterave', 'betteraves', 'beetroot'],
    category: 'vegetable',
  },
  {
    id: 'leek',
    displayName: 'Leek / Poireau',
    searchTerms: ['leek', 'leeks', 'poireau', 'poireaux'],
    category: 'vegetable',
  },
  {
    id: 'avocado',
    displayName: 'Avocado / Avocat',
    searchTerms: ['avocado', 'avocat', 'avocats'],
    category: 'vegetable',
  },
  {
    id: 'squash',
    displayName: 'Squash / Courge',
    searchTerms: ['squash', 'courge', 'butternut', 'butternut squash', 'acorn squash', 'spaghetti squash'],
    category: 'vegetable',
  },
  {
    id: 'kale',
    displayName: 'Kale / Chou frisé',
    searchTerms: ['kale', 'chou frisé', 'chou kale'],
    category: 'vegetable',
  },

  // === FRUITS ===
  {
    id: 'apple',
    displayName: 'Apple / Pomme',
    searchTerms: ['apple', 'apples', 'pomme', 'pommes', 'gala', 'granny smith', 'honeycrisp'],
    category: 'fruit',
  },
  {
    id: 'banana',
    displayName: 'Banana / Banane',
    searchTerms: ['banana', 'bananas', 'banane', 'bananes'],
    category: 'fruit',
  },
  {
    id: 'lemon',
    displayName: 'Lemon / Citron',
    searchTerms: ['lemon', 'lemons', 'citron', 'citrons', 'lemon juice', 'jus de citron'],
    category: 'fruit',
  },
  {
    id: 'lime',
    displayName: 'Lime / Lime',
    searchTerms: ['lime', 'limes', 'lime juice', 'jus de lime'],
    category: 'fruit',
  },
  {
    id: 'orange',
    displayName: 'Orange',
    searchTerms: ['orange', 'oranges', 'orange juice', 'jus d\'orange'],
    category: 'fruit',
  },
  {
    id: 'strawberry',
    displayName: 'Strawberry / Fraise',
    searchTerms: ['strawberry', 'strawberries', 'fraise', 'fraises'],
    category: 'fruit',
  },
  {
    id: 'blueberry',
    displayName: 'Blueberry / Bleuet',
    searchTerms: ['blueberry', 'blueberries', 'bleuet', 'bleuets', 'myrtille', 'myrtilles'],
    category: 'fruit',
  },
  {
    id: 'raspberry',
    displayName: 'Raspberry / Framboise',
    searchTerms: ['raspberry', 'raspberries', 'framboise', 'framboises'],
    category: 'fruit',
  },
  {
    id: 'grape',
    displayName: 'Grape / Raisin',
    searchTerms: ['grape', 'grapes', 'raisin', 'raisins'],
    category: 'fruit',
  },
  {
    id: 'mango',
    displayName: 'Mango / Mangue',
    searchTerms: ['mango', 'mangue', 'mangoes'],
    category: 'fruit',
  },
  {
    id: 'pineapple',
    displayName: 'Pineapple / Ananas',
    searchTerms: ['pineapple', 'ananas'],
    category: 'fruit',
  },
  {
    id: 'peach',
    displayName: 'Peach / Pêche',
    searchTerms: ['peach', 'peaches', 'pêche', 'pêches'],
    category: 'fruit',
  },

  // === DAIRY ===
  {
    id: 'milk',
    displayName: 'Milk / Lait',
    searchTerms: ['milk', 'lait', 'whole milk', 'lait entier', '2% milk', 'skim milk', 'lait écrémé'],
    category: 'dairy',
  },
  {
    id: 'cheese',
    displayName: 'Cheese / Fromage',
    searchTerms: ['cheese', 'fromage', 'cheddar', 'mozzarella', 'parmesan', 'feta', 'goat cheese', 'fromage de chèvre', 'gruyère', 'swiss'],
    category: 'dairy',
  },
  {
    id: 'butter',
    displayName: 'Butter / Beurre',
    searchTerms: ['butter', 'beurre', 'unsalted butter', 'beurre non salé'],
    category: 'dairy',
  },
  {
    id: 'cream',
    displayName: 'Cream / Crème',
    searchTerms: ['cream', 'crème', 'heavy cream', 'crème épaisse', 'whipping cream', 'crème fouettée', 'sour cream', 'crème sure'],
    category: 'dairy',
  },
  {
    id: 'yogurt',
    displayName: 'Yogurt / Yogourt',
    searchTerms: ['yogurt', 'yogourt', 'greek yogurt', 'yogourt grec', 'plain yogurt'],
    category: 'dairy',
  },
  {
    id: 'cream-cheese',
    displayName: 'Cream Cheese / Fromage à la crème',
    searchTerms: ['cream cheese', 'fromage à la crème', 'philadelphia'],
    category: 'dairy',
  },

  // === GRAINS ===
  {
    id: 'rice',
    displayName: 'Rice / Riz',
    searchTerms: ['rice', 'riz', 'white rice', 'riz blanc', 'brown rice', 'riz brun', 'basmati', 'jasmine rice'],
    category: 'grain',
  },
  {
    id: 'pasta',
    displayName: 'Pasta / Pâtes',
    searchTerms: ['pasta', 'pâtes', 'pâte', 'spaghetti', 'penne', 'fusilli', 'macaroni', 'linguine', 'fettuccine', 'noodles', 'nouilles'],
    category: 'grain',
  },
  {
    id: 'bread',
    displayName: 'Bread / Pain',
    searchTerms: ['bread', 'pain', 'baguette', 'whole wheat bread', 'pain de blé entier', 'sourdough', 'toast'],
    category: 'grain',
  },
  {
    id: 'flour',
    displayName: 'Flour / Farine',
    searchTerms: ['flour', 'farine', 'all-purpose flour', 'farine tout usage', 'whole wheat flour', 'farine de blé entier'],
    category: 'grain',
  },
  {
    id: 'oats',
    displayName: 'Oats / Avoine',
    searchTerms: ['oats', 'avoine', 'oatmeal', 'gruau', 'rolled oats', 'flocons d\'avoine'],
    category: 'grain',
  },
  {
    id: 'quinoa',
    displayName: 'Quinoa',
    searchTerms: ['quinoa'],
    category: 'grain',
  },
  {
    id: 'couscous',
    displayName: 'Couscous',
    searchTerms: ['couscous'],
    category: 'grain',
  },
  {
    id: 'tortilla',
    displayName: 'Tortilla',
    searchTerms: ['tortilla', 'tortillas', 'flour tortilla', 'corn tortilla', 'wrap'],
    category: 'grain',
  },

  // === PANTRY ===
  {
    id: 'olive-oil',
    displayName: 'Olive Oil / Huile d\'olive',
    searchTerms: ['olive oil', 'huile d\'olive', 'huile olive', 'extra virgin olive oil'],
    category: 'pantry',
  },
  {
    id: 'vegetable-oil',
    displayName: 'Vegetable Oil / Huile végétale',
    searchTerms: ['vegetable oil', 'huile végétale', 'canola oil', 'huile de canola', 'cooking oil'],
    category: 'pantry',
  },
  {
    id: 'sugar',
    displayName: 'Sugar / Sucre',
    searchTerms: ['sugar', 'sucre', 'brown sugar', 'cassonade', 'white sugar', 'sucre blanc', 'powdered sugar', 'sucre en poudre'],
    category: 'pantry',
  },
  {
    id: 'salt',
    displayName: 'Salt / Sel',
    searchTerms: ['salt', 'sel', 'sea salt', 'sel de mer', 'kosher salt'],
    category: 'pantry',
  },
  {
    id: 'pepper',
    displayName: 'Black Pepper / Poivre',
    searchTerms: ['pepper', 'black pepper', 'poivre', 'poivre noir', 'ground pepper'],
    category: 'pantry',
  },
  {
    id: 'vinegar',
    displayName: 'Vinegar / Vinaigre',
    searchTerms: ['vinegar', 'vinaigre', 'white vinegar', 'vinaigre blanc', 'balsamic vinegar', 'vinaigre balsamique', 'apple cider vinegar'],
    category: 'pantry',
  },
  {
    id: 'soy-sauce',
    displayName: 'Soy Sauce / Sauce soya',
    searchTerms: ['soy sauce', 'sauce soya', 'sauce soja', 'tamari'],
    category: 'condiment',
  },
  {
    id: 'honey',
    displayName: 'Honey / Miel',
    searchTerms: ['honey', 'miel'],
    category: 'pantry',
  },
  {
    id: 'maple-syrup',
    displayName: 'Maple Syrup / Sirop d\'érable',
    searchTerms: ['maple syrup', 'sirop d\'érable', 'sirop érable'],
    category: 'pantry',
  },
  {
    id: 'tomato-sauce',
    displayName: 'Tomato Sauce / Sauce tomate',
    searchTerms: ['tomato sauce', 'sauce tomate', 'marinara', 'tomato paste', 'pâte de tomate', 'passata'],
    category: 'pantry',
  },
  {
    id: 'chicken-broth',
    displayName: 'Chicken Broth / Bouillon de poulet',
    searchTerms: ['chicken broth', 'bouillon de poulet', 'chicken stock', 'broth', 'bouillon'],
    category: 'pantry',
  },
  {
    id: 'coconut-milk',
    displayName: 'Coconut Milk / Lait de coco',
    searchTerms: ['coconut milk', 'lait de coco', 'coconut cream', 'crème de coco'],
    category: 'pantry',
  },
  {
    id: 'beans',
    displayName: 'Beans / Haricots',
    searchTerms: ['beans', 'haricots', 'black beans', 'haricots noirs', 'kidney beans', 'haricots rouges', 'chickpeas', 'pois chiches', 'lentils', 'lentilles', 'white beans'],
    category: 'pantry',
  },
  {
    id: 'canned-tomatoes',
    displayName: 'Canned Tomatoes / Tomates en conserve',
    searchTerms: ['canned tomatoes', 'tomates en conserve', 'diced tomatoes', 'tomates en dés', 'crushed tomatoes', 'tomates broyées'],
    category: 'pantry',
  },

  // === HERBS & SPICES ===
  {
    id: 'basil',
    displayName: 'Basil / Basilic',
    searchTerms: ['basil', 'basilic', 'fresh basil', 'basilic frais'],
    category: 'herb',
  },
  {
    id: 'parsley',
    displayName: 'Parsley / Persil',
    searchTerms: ['parsley', 'persil', 'fresh parsley', 'persil frais'],
    category: 'herb',
  },
  {
    id: 'cilantro',
    displayName: 'Cilantro / Coriandre',
    searchTerms: ['cilantro', 'coriandre', 'coriander', 'fresh cilantro'],
    category: 'herb',
  },
  {
    id: 'thyme',
    displayName: 'Thyme / Thym',
    searchTerms: ['thyme', 'thym', 'fresh thyme'],
    category: 'herb',
  },
  {
    id: 'rosemary',
    displayName: 'Rosemary / Romarin',
    searchTerms: ['rosemary', 'romarin', 'fresh rosemary'],
    category: 'herb',
  },
  {
    id: 'oregano',
    displayName: 'Oregano / Origan',
    searchTerms: ['oregano', 'origan'],
    category: 'herb',
  },
  {
    id: 'mint',
    displayName: 'Mint / Menthe',
    searchTerms: ['mint', 'menthe', 'fresh mint', 'menthe fraîche'],
    category: 'herb',
  },
  {
    id: 'ginger',
    displayName: 'Ginger / Gingembre',
    searchTerms: ['ginger', 'gingembre', 'fresh ginger', 'gingembre frais', 'ground ginger'],
    category: 'herb',
  },
  {
    id: 'cumin',
    displayName: 'Cumin',
    searchTerms: ['cumin', 'ground cumin', 'cumin moulu'],
    category: 'herb',
  },
  {
    id: 'paprika',
    displayName: 'Paprika',
    searchTerms: ['paprika', 'smoked paprika', 'paprika fumé'],
    category: 'herb',
  },
  {
    id: 'cinnamon',
    displayName: 'Cinnamon / Cannelle',
    searchTerms: ['cinnamon', 'cannelle', 'ground cinnamon'],
    category: 'herb',
  },
  {
    id: 'vanilla',
    displayName: 'Vanilla / Vanille',
    searchTerms: ['vanilla', 'vanille', 'vanilla extract', 'extrait de vanille'],
    category: 'herb',
  },
  {
    id: 'chili',
    displayName: 'Chili / Piment',
    searchTerms: ['chili', 'piment', 'chili pepper', 'hot pepper', 'piment fort', 'jalapeño', 'cayenne', 'chili flakes', 'flocons de piment'],
    category: 'herb',
  },

  // === CONDIMENTS ===
  {
    id: 'mustard',
    displayName: 'Mustard / Moutarde',
    searchTerms: ['mustard', 'moutarde', 'dijon mustard', 'moutarde de dijon', 'yellow mustard'],
    category: 'condiment',
  },
  {
    id: 'mayonnaise',
    displayName: 'Mayonnaise',
    searchTerms: ['mayonnaise', 'mayo'],
    category: 'condiment',
  },
  {
    id: 'ketchup',
    displayName: 'Ketchup',
    searchTerms: ['ketchup'],
    category: 'condiment',
  },
  {
    id: 'hot-sauce',
    displayName: 'Hot Sauce / Sauce piquante',
    searchTerms: ['hot sauce', 'sauce piquante', 'sriracha', 'tabasco'],
    category: 'condiment',
  },
  {
    id: 'worcestershire',
    displayName: 'Worcestershire Sauce',
    searchTerms: ['worcestershire', 'worcestershire sauce', 'sauce worcestershire'],
    category: 'condiment',
  },
  {
    id: 'sesame-oil',
    displayName: 'Sesame Oil / Huile de sésame',
    searchTerms: ['sesame oil', 'huile de sésame', 'huile sesame'],
    category: 'condiment',
  },
];

/**
 * Category colors for chip styling
 */
export const CATEGORY_COLORS: Record<IngredientCategory, string> = {
  protein: '#FFEBEE', // Light red/pink
  vegetable: '#E8F5E9', // Light green
  fruit: '#FFF3E0', // Light orange
  dairy: '#FFF8E1', // Light cream/yellow
  grain: '#EFEBE9', // Light brown
  pantry: '#F5F5F5', // Light gray
  herb: '#E8F5E9', // Light green (same as vegetable)
  condiment: '#FCE4EC', // Light pink
};

/**
 * Search ingredients by query
 * Returns matching ingredients sorted by relevance
 */
export function searchIngredients(
  query: string,
  maxResults: number = 8
): SearchableIngredient[] {
  if (!query.trim()) return [];

  const normalizedQuery = query.toLowerCase().trim();

  // Score each ingredient based on match quality
  const scored = SEARCHABLE_INGREDIENTS.map((ingredient) => {
    let score = 0;

    // Check display name match (highest priority)
    if (ingredient.displayName.toLowerCase().includes(normalizedQuery)) {
      score += 100;
      // Bonus for starting with query
      if (ingredient.displayName.toLowerCase().startsWith(normalizedQuery)) {
        score += 50;
      }
    }

    // Check search terms
    for (const term of ingredient.searchTerms) {
      if (term.includes(normalizedQuery)) {
        score += 30;
        // Bonus for exact match
        if (term === normalizedQuery) {
          score += 50;
        }
        // Bonus for starting with query
        if (term.startsWith(normalizedQuery)) {
          score += 20;
        }
        break; // Only count first matching term
      }
    }

    return { ingredient, score };
  });

  // Filter and sort by score
  return scored
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, maxResults)
    .map((item) => item.ingredient);
}

/**
 * Get chip color for an ingredient
 */
export function getIngredientChipColor(ingredientId: string): string {
  const ingredient = SEARCHABLE_INGREDIENTS.find((i) => i.id === ingredientId);
  if (!ingredient) return CATEGORY_COLORS.pantry;
  return CATEGORY_COLORS[ingredient.category];
}

/**
 * Get chip color by category
 */
export function getCategoryChipColor(category: IngredientCategory): string {
  return CATEGORY_COLORS[category];
}

/**
 * Find ingredient by ID
 */
export function getIngredientById(id: string): SearchableIngredient | undefined {
  return SEARCHABLE_INGREDIENTS.find((i) => i.id === id);
}

/**
 * Expand a search query to all bilingual synonyms.
 * e.g. "agneau" → ["lamb", "agneau", "lamb chop", "côtelette d'agneau"]
 * Returns [query] as-is if no match found in the ingredient dictionary.
 */
export function expandSearchQuery(query: string): string[] {
  const normalized = query.toLowerCase().trim();
  const match = SEARCHABLE_INGREDIENTS.find((ing) =>
    ing.searchTerms.some((term) => term.toLowerCase() === normalized)
  );
  if (!match) return [normalized];
  return [...new Set(match.searchTerms.map((t) => t.toLowerCase()))];
}

/**
 * Check if a recipe ingredient matches a searchable ingredient
 */
export function matchesSearchableIngredient(
  recipeIngredient: string,
  searchableIngredient: SearchableIngredient
): boolean {
  const normalized = recipeIngredient.toLowerCase();
  return searchableIngredient.searchTerms.some((term) =>
    normalized.includes(term.toLowerCase())
  );
}

export default {
  SEARCHABLE_INGREDIENTS,
  CATEGORY_COLORS,
  searchIngredients,
  getIngredientChipColor,
  getCategoryChipColor,
  getIngredientById,
  matchesSearchableIngredient,
};
